import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useState, useEffect } from 'react';
import { firebaseService } from '../services/firebaseService';
import { apiService } from '../services/apiService';
import { notificationManager } from '../services/notification';
import { posthogService } from '../services/posthogService';
import { getEmojiForOption } from '../utils/utils';

export const Route = createFileRoute('/profile')({
  component: ProfilePage,
});

function ProfilePage() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileOptions, setProfileOptions] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    nationality: '',
    employment_type: '',
    salary_range: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load profile options first
      const options = await apiService.getProfileOptions();
      setProfileOptions(options);
      
      // Then load user profile
      await loadProfile();
    } catch (error) {
      console.error('Error loading data:', error);
      notificationManager.error(error.message || 'Failed to load data');
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    try {
      const currentUser = await firebaseService.getCurrentUser();
      
      if (!currentUser) {
        notificationManager.error('Please sign in to view your profile');
        navigate({ to: '/' });
        return;
      }

      const idToken = await firebaseService.getIdToken();
      const profileData = await apiService.getUserProfile(idToken);
      
      setProfile(profileData);
      setFormData({
        email: profileData.email || '',
        name: profileData.name || '',
        nationality: profileData.nationality || '',
        employment_type: profileData.employment_type || '',
        salary_range: profileData.salary_range || '',
      });
      
      // Track profile viewed
      posthogService.profileViewed();
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading profile:', error);
      notificationManager.error(error.message || 'Failed to load profile');
      setLoading(false);
      // Don't navigate away on error, let user see the error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      const idToken = await firebaseService.getIdToken();
      
      // Track which fields were updated
      const updatedFields = {};
      if (formData.email !== profile.email) updatedFields.email = formData.email;
      if (formData.name !== profile.name) updatedFields.name = formData.name;
      if (formData.nationality !== profile.nationality) updatedFields.nationality = formData.nationality;
      if (formData.employment_type !== profile.employment_type) updatedFields.employment_type = formData.employment_type;
      if (formData.salary_range !== profile.salary_range) updatedFields.salary_range = formData.salary_range;
      
      const updatedProfile = await apiService.updateUserProfile(idToken, formData);
      
      setProfile(updatedProfile);
      
      // Track profile updated
      if (Object.keys(updatedFields).length > 0) {
        posthogService.profileUpdated(updatedFields);
      }
      
      notificationManager.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      notificationManager.error(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Get available options (exclude currently selected)
  const getAvailableOptions = (field, allOptions) => {
    if (!allOptions) return [];
    const currentValue = formData[field];
    return allOptions.filter(opt => opt.key !== currentValue);
  };

  if (loading || !profileOptions) {
    return (
      <div className='bg-white rounded-2xl p-8 shadow-xl text-center'>
        <div className='flex items-center justify-center gap-3'>
          <span className='text-sm text-gray-600'>Loading profile...</span>
          <div className='w-4 h-4 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin'></div>
        </div>
      </div>
    );
  }

  // Get display label for current value
  const getDisplayLabel = (field, fieldKey) => {
    const optionsList = profileOptions[fieldKey];
    if (!optionsList) return '';
    const currentValue = formData[field];
    const option = optionsList.find(opt => opt.key === currentValue);
    return option ? option.label : 'Not set';
  };

  return (
    <div className='bg-white rounded-2xl p-8 shadow-xl'>
      <div className='max-w-2xl mx-auto'>
        <div className='text-sm text-gray-500 mb-2 uppercase tracking-wide text-center'>
          My Profile
        </div>
        <h2 className='text-2xl font-light text-gray-900 mb-2 text-center'>
          Account Settings
        </h2>
        <p className='text-sm text-gray-600 mb-8 text-center'>
          Manage your personal information and preferences
        </p>

        {/* Profile Stats */}
        <div className='bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8'>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <div className='text-xs text-gray-500 uppercase tracking-wider mb-1'>Phone Number</div>
              <div className='text-sm text-gray-900 font-mono'>{profile?.phone_number}</div>
            </div>
            <div>
              <div className='text-xs text-gray-500 uppercase tracking-wider mb-1'>Completed Sessions</div>
              <div className='text-sm text-gray-900'>{profile?.completed_sessions || 0}</div>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <form onSubmit={handleSubmit} className='space-y-6'>
          {/* Email */}
          <div>
            <label className='block text-xs text-gray-500 uppercase tracking-wide mb-2'>
              Email (Optional)
            </label>
            <input
              type='email'
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder='your@email.com'
              className='w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-200 transition-colors'
            />
          </div>

          {/* Name */}
          <div>
            <label className='block text-xs text-gray-500 uppercase tracking-wide mb-2'>
              Name (Optional)
            </label>
            <input
              type='text'
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder='Your Name'
              className='w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-200 transition-colors'
            />
          </div>

          {/* Nationality */}
          <div>
            <label className='block text-xs text-gray-500 uppercase tracking-wide mb-2'>
              Nationality / Visa Status
            </label>
            <div className='relative'>
              <div className='text-sm text-gray-900 mb-2 font-medium'>
                Current: {getEmojiForOption('nationality_visa', formData.nationality)} {getDisplayLabel('nationality', 'nationality_visa')}
              </div>
              <select
                value={formData.nationality}
                onChange={(e) => handleChange('nationality', e.target.value)}
                className='w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-200 transition-colors bg-white'
              >
                <option value=''>Change nationality...</option>
                {getAvailableOptions('nationality', profileOptions.nationality_visa).map((option) => {
                  const emoji = getEmojiForOption('nationality_visa', option.key);
                  return (
                    <option key={option.key} value={option.key}>
                      {emoji ? `${emoji} ` : ''}{option.label}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Employment Type */}
          <div>
            <label className='block text-xs text-gray-500 uppercase tracking-wide mb-2'>
              Employment Type
            </label>
            <div className='relative'>
              <div className='text-sm text-gray-900 mb-2 font-medium'>
                Current: {getEmojiForOption('employment_type', formData.employment_type)} {getDisplayLabel('employment_type', 'employment_type')}
              </div>
              <select
                value={formData.employment_type}
                onChange={(e) => handleChange('employment_type', e.target.value)}
                className='w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-200 transition-colors bg-white'
              >
                <option value=''>Change employment type...</option>
                {getAvailableOptions('employment_type', profileOptions.employment_type).map((option) => {
                  const emoji = getEmojiForOption('employment_type', option.key);
                  return (
                    <option key={option.key} value={option.key}>
                      {emoji ? `${emoji} ` : ''}{option.label}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Salary Range */}
          <div>
            <label className='block text-xs text-gray-500 uppercase tracking-wide mb-2'>
              Monthly Salary Range
            </label>
            <div className='relative'>
              <div className='text-sm text-gray-900 mb-2 font-medium'>
                Current: {getEmojiForOption('monthly_salary', formData.salary_range)} {getDisplayLabel('salary_range', 'monthly_salary')}
              </div>
              <select
                value={formData.salary_range}
                onChange={(e) => handleChange('salary_range', e.target.value)}
                className='w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-200 transition-colors bg-white'
              >
                <option value=''>Change salary range...</option>
                {getAvailableOptions('salary_range', profileOptions.monthly_salary).map((option) => {
                  const emoji = getEmojiForOption('monthly_salary', option.key);
                  return (
                    <option key={option.key} value={option.key}>
                      {emoji ? `${emoji} ` : ''}{option.label}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type='submit'
            disabled={saving}
            className='w-full py-3 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
          >
            {saving ? (
              <>
                <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                <span>Saving...</span>
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ProfilePage;
